import { forwardRef, type ElementType } from "react";
import { CoreLink } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";
import { forwardRefGenerics } from "@boi/react-utils";

import * as S from "./LinkWithIcon.styled";
import { type LinkBaseProps } from "../LinkBase";

export type LinkWithIconProps<T extends ElementType> = Omit<
  LinkBaseProps<T>,
  "display" | "children"
> & {
  variant?: CoreLink.Variant;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  children: string;
};

interface WithForwardRefType extends React.FC<LinkWithIconProps<ElementType>> {
  <T extends ElementType>(props: LinkWithIconProps<T>): ReturnType<
    React.FC<LinkWithIconProps<T>>
  >;
}

const LinkWithIcon: WithForwardRefType = forwardRefGenerics((props, ref) => {
  const {
    isUnderlined = true,
    isDashed = false,
    variant = CoreLink.DEFAULT_VARIANT,
    startIcon,
    endIcon,
    children,
    testID = "link",
    ...linkBaseProps
  } = props;

  const { tokens } = useDesignTokens();
  const { link } = tokens;

  return (
    <S.Wrapper
      {...linkBaseProps}
      ref={ref}
      display="inline-flex"
      testID={testID}
    >
      <S.Container>
        {startIcon && (
          <S.IconWrapper
            $variant={variant}
            data-testid={`${testID}-start-icon-wrapper`}
          >
            {startIcon}
          </S.IconWrapper>
        )}

        <S.Text
          component="span"
          color="inherit"
          variant={variant}
          fontFamily={link.typography.fontFamily}
          $isUnderlined={isUnderlined}
          $isDashed={isDashed}
        >
          {children}
        </S.Text>

        {endIcon && (
          <S.IconWrapper
            $variant={variant}
            data-testid={`${testID}-end-icon-wrapper`}
          >
            {endIcon}
          </S.IconWrapper>
        )}
      </S.Container>
    </S.Wrapper>
  );
});

LinkWithIcon.displayName = "LinkWithIcon";

export default LinkWithIcon;
